import { isBoolean, isNaN, isString } from 'lodash-es';

import commonBaseService from '../commonBaseService';
import commonDateService from '../commonDateService';
import orderScripService from './orderForm/orderScripService';

const CURRENCY_LTP_DECIMAL_POINT = 4;
const DEFAULT_LTP_DECIMAL_POINT = 2;

const _isValidFeedValue = (value) => {
	if (commonBaseService.isEmptyOrUndefined(value)) {
		return false;
	} else {
		const valueInNumber = Number(value);
		const isValidNumber = !isNaN(valueInNumber);
		return isValidNumber;
	}
};

// validate the formatLtpValue arguments
const _isValidLtpArgs = (ltp, divider, isCurrency) => {
	const isValidLtp = _isValidFeedValue(ltp);
	const dividerInNumber = Number(divider);
	const isValidDivider = _isValidFeedValue(divider) && dividerInNumber !== 0;
	const isValidCurrencyFlag = isBoolean(isCurrency);
	const isValidLtpArgs = isValidLtp && isValidDivider && isValidCurrencyFlag;
	return isValidLtpArgs;
};

// validate the formatOneDayChangeValue arguments
const _isValidOneDayChangeArgs = (oneDayChange, decimalPlaces) => {
	const isValidOneDayChange = _isValidFeedValue(oneDayChange);
	const isValidDecimalPoint = _isValidFeedValue(decimalPlaces);
	const isValidOneDayChangeArgs = isValidOneDayChange && isValidDecimalPoint;
	return isValidOneDayChangeArgs;
};

// validate the formatOneDayChangePercValue arguments
const _isValidOneDayChangePercArgs = (oneDayChangePerc, decimalPlaces) => {
	const isValidoneDayChangePerc = _isValidFeedValue(oneDayChangePerc);
	const isValidDecimalPoint = _isValidFeedValue(decimalPlaces);
	const isValidoneDayChangePercArgs =
		isValidoneDayChangePerc && isValidDecimalPoint;
	return isValidoneDayChangePercArgs;
};

/*
	- this returns the ltp value in decimal format.
	- use this value for computation.
	input:
	 ltp = 133015;
	 divider = 100;
	output: 1330.15
*/
const formatLtpValue = (ltp = '', divider = 1, isCurrency = false) => {
	if (!_isValidLtpArgs(ltp, divider, isCurrency)) return '-';
	const decimalPlaces = isCurrency
		? CURRENCY_LTP_DECIMAL_POINT
		: DEFAULT_LTP_DECIMAL_POINT;
	let ltpValue = ltp / divider;
	ltpValue = commonBaseService.getNumberInDecimalFormat(
		ltpValue,
		decimalPlaces,
	);
	return ltpValue;
};

/*
	- this returns the oneDayChange value in decimal format.
	- use this value for computation.
	input: 1234
	output: 1234.00

	input: -124.2321
	output: -124.23
*/
const formatOneDayChangeValue = (oneDayChange = '', decimalPlaces = 2) => {
	if (!_isValidOneDayChangeArgs(oneDayChange, decimalPlaces)) return '';
	const oneDayChangeValue = commonBaseService.getNumberInDecimalFormat(
		oneDayChange,
		decimalPlaces,
	);
	return oneDayChangeValue;
};

const handleNonTradedScripSocketdata = (data = {}) => {
	const { last_trade_price = '-', close_price = '-' } = data;
	if (isNonTradedScrip(last_trade_price)) {
		return {
			...data,
			last_trade_price: close_price,
			netchangefromprevclose: 0,
			one_day_change: 0,
		};
	} else {
		return data;
	}
};

// checks if the last_traded_price is zero
const isNonTradedScrip = (last_trade_price = '') =>
	commonBaseService.isValidNumber(last_trade_price) &&
	Number(last_trade_price) === 0;

/*
	- this returns the oneDayChangePerc in decimal format.
	- use this value for computation.
	input: 1234
	output: 1234.00

	input: -124.2321
	output: -124.23
*/
const formatOneDayChangePercValue = (
	oneDayChangePerc = '',
	decimalPlaces = 2,
) => {
	if (!_isValidOneDayChangePercArgs(oneDayChangePerc, decimalPlaces))
		return '';
	const oneDayChangePercValue = commonBaseService.getNumberInDecimalFormat(
		oneDayChangePerc,
		decimalPlaces,
	);
	return oneDayChangePercValue;
};

/*
	The below function adds ₹ sign and returns the value in decimal format.
	input:
	 ltp = 133015;
	 divider = 100;
	output: ₹1330.15
*/
const formatLtpViewValue = (
	ltp = '',
	divider = 1,
	isIndex = false,
	isCurrency = false,
) => {
	const ltpValue = formatLtpValue(ltp, divider, isCurrency);
	if (commonBaseService.isEmptyOrUndefined(ltpValue)) return '-';
	return ltpValue;
};

/*
	The below function adds +/- sign from the number and returns the
	value in decimal format.
	input: 1234
	output: +1234.00

	input: -124.2321
	output: -124.23
*/
const formatOneDayChangeViewValue = (oneDayChange = '', decimalPlaces = 2) => {
	let oneDayChangeValue = formatOneDayChangeValue(
		oneDayChange,
		decimalPlaces,
	);
	if (commonBaseService.isEmptyOrUndefined(oneDayChangeValue)) return '-';
	if (oneDayChangeValue > 0) {
		if (oneDayChangeValue.includes('+')) {
			return oneDayChangeValue;
		} else {
			return (oneDayChangeValue = `+${oneDayChangeValue}`);
		}
	} else {
		return oneDayChangeValue;
	}
};

/*
	The below function removes +/- sign from the number and returns the
	value in decimal format.
	Example:
	input: 1234
	output: (1234.00%)

	input: -124.2321
	output: (124.23%)
*/
const formatOneDayChangePercViewValue = (
	oneDayChangePerc = '',
	decimalPlaces = 2,
) => {
	let oneDayChangePercValue = formatOneDayChangePercValue(
		oneDayChangePerc,
		decimalPlaces,
	);
	if (commonBaseService.isEmptyOrUndefined(oneDayChangePercValue)) return '-';
	oneDayChangePercValue = oneDayChangePercValue.replace(/[^\d.]/g, '');
	oneDayChangePercValue = `(${oneDayChangePercValue}%)`;
	return oneDayChangePercValue;
};

/*
	The below function formates volume of the derivaties the number and returns the
	value in compact formate based on the locale.
	Example:
	input: 60,000
	output: (1234.00%)

	input: -124.2321
	output: (124.23%)
*/

const formatVolume = (volume, decimalPlaces = 2) => {
	if (
		commonBaseService.isEmptyOrUndefined(volume) ||
		!commonBaseService.isValidNumber(volume)
	) {
		return '-';
	}

	let tradingVolume = Number(volume);
	let multiplierSymbol = '';
	const croreMultiplier = Math.pow(10, 7);
	const lakhMultiplier = Math.pow(10, 5);
	const thousandMultiplier = Math.pow(10, 3);

	if (tradingVolume > croreMultiplier) {
		multiplierSymbol = 'Cr';
		tradingVolume = tradingVolume / croreMultiplier;
	} else if (tradingVolume > lakhMultiplier) {
		multiplierSymbol = 'L';
		tradingVolume = tradingVolume / lakhMultiplier;
	} else if (tradingVolume > thousandMultiplier) {
		multiplierSymbol = 'K';
		tradingVolume = tradingVolume / thousandMultiplier;
	}
	let formatedTradingVolume = commonBaseService.formatNumber(tradingVolume, {
		decimalPlaces: volume <= 1000 ? 0 : 2,
		retainNegativeSign: false,
	});
	formatedTradingVolume = !commonBaseService.isEmptyOrUndefined(
		multiplierSymbol,
	)
		? `${formatedTradingVolume} ${multiplierSymbol}`
		: formatedTradingVolume;
	return formatedTradingVolume;
};

const formatCircuitValues = (daily_price_range, isCurrencyScrip) => {
	const defaultCircuitValues = { lower_circuit: '-', upper_circuit: '-' };
	if (
		commonBaseService.isEmptyOrUndefined(daily_price_range) ||
		!isString(daily_price_range)
	) {
		return defaultCircuitValues;
	}

	let [lower_circuit, upper_circuit] = daily_price_range.split('-');
	const decimalPlaces = isCurrencyScrip ? 4 : 2;
	if (
		commonBaseService.isEmptyOrUndefined(lower_circuit) ||
		commonBaseService.isEmptyOrUndefined(upper_circuit)
	) {
		return defaultCircuitValues;
	}
	lower_circuit = Number(lower_circuit);
	upper_circuit = Number(upper_circuit);
	lower_circuit = commonBaseService.formatNumber(lower_circuit, {
		decimalPlaces: decimalPlaces,
	});
	upper_circuit = commonBaseService.formatNumber(upper_circuit, {
		decimalPlaces: decimalPlaces,
	});
	return { lower_circuit, upper_circuit };
};

const getSupportedDecimals = (instrumentName) => {
	return orderScripService.isCurrencyDerivatesScrip(instrumentName)
		? CURRENCY_LTP_DECIMAL_POINT
		: DEFAULT_LTP_DECIMAL_POINT;
};

/*
	The below function formates the last traded time
	Example:
	input: 2023-06-26 155946 (yyyy-MM-dd hhmmss)
	output: 6 Jun 2023 03:59:46 pm (dd MM yyyy hh:mm:ss a)
*/

const formatLastTradedTime = (lastTradedTime) => {
	if (
		commonBaseService.isEmptyOrUndefined(lastTradedTime) ||
		!commonDateService.isValidDateTime(lastTradedTime)
	) {
		return '-';
	}
	const formatedTime = commonDateService.formatDateTime(
		lastTradedTime,
		'yyyy-MM-dd HHmmss',
		'dd MMM yyyy, hh:mm:ss a',
	);
	return formatedTime;
};

const calculateChangeInOi = (oi, oiChangePerc) => {
	if (
		commonBaseService.isEmptyOrUndefined(oi) ||
		commonBaseService.isEmptyOrUndefined(oiChangePerc)
	) {
		return '-';
	}

	const totalOi = Number(oi);
	const changePerc = Number(oiChangePerc);
	if (
		!commonBaseService.isValidNumber(totalOi) ||
		!commonBaseService.isValidNumber(changePerc)
	) {
		return '-';
	}
	const changeInOiValue = (totalOi * changePerc) / 100;

	const changeInOiNoSign = removeNegativeOrPositiveSign(
		changeInOiValue.toString(),
	);
	const formatedChangeInOi = formatVolume(changeInOiNoSign);
	const addSign = changeInOiValue > 0 ? '+' : '-';
	return `${changeInOiValue !== 0 ? addSign : ''}${formatedChangeInOi}`;
};

/**
 * This function removes the negative or positive sign from the number but retains the decimal point
 * @param numString
 * @returns {string}
 */
const removeNegativeOrPositiveSign = (numString = '') => {
	if (!commonBaseService.isEmptyOrUndefined(numString)) {
		const result = numString.replace(/[^\d.]/g, '');
		return result;
	}
};

export const ltpFeedService = {
	formatLtpValue,
	formatOneDayChangeValue,
	formatOneDayChangePercValue,
	formatLtpViewValue,
	formatOneDayChangeViewValue,
	formatOneDayChangePercViewValue,
	getSupportedDecimals,
	formatVolume,
	formatCircuitValues,
	calculateChangeInOi,
	formatLastTradedTime,
	handleNonTradedScripSocketdata,
	removeNegativeOrPositiveSign,
};
